import axios from "axios";

import API_URLS from "../../constants/api-urls";
import storage from "../../utils/storage";
import AuthAPI from "./authApi";
import FarmerApi from "./farmerApi";
import StrippingApi from "./strippingApi";
import Fermentation from "./fermentationApi";
import SpiritApi from "./spiritApi";
import UserAPI from "./userApi";
import BottleAPI from "./bottleApi";

import { logger, customToast } from "../../utils/utils";
import { API_STATUS, API_ERROR_MSG } from "../../constants/constants";
import ProofingApi from "./proofingApi";
import CaskAndAgingApi from "./caskAndAgingApi";
import CaskMasterApi from "./caskMasterApi";
import BottlingApi from "./bottlingApi";
import CaneVarietyApi from "./caneVarietyApi";
import SupplierApi from "./supplierApi";
import MolassesApi from "./molassesApi";
import RumTypeApi from "./rumTypeApi";
import GuestAPI from "./guestApi";
import NutrientsApi from "./nutrientsApi";

class API {
  __auth = new AuthAPI();
  __farmer = new FarmerApi();
  __supplier = new SupplierApi();
  __molasses = new MolassesApi();
  __user = new UserAPI();
  __fermentation = new Fermentation();
  __stripping = new StrippingApi();
  __spirit = new SpiritApi();
  __proofing = new ProofingApi();
  __caskandaging = new CaskAndAgingApi();
  __caskmaster = new CaskMasterApi();
  __bottling = new BottlingApi();
  __canevariety = new CaneVarietyApi();
  __rumType = new RumTypeApi();
  __nutrients = new NutrientsApi();
  __bottle = new BottleAPI();
  __guest = new GuestAPI();

  api = axios.create({
    baseURL: API_URLS.BASE,
    headers: {
      accept: "application/json",
      "ngrok-skip-browser-warning": "true",
    },
  });

  get auth() {
    return this.__auth;
  }

  get farmer() {
    return this.__farmer;
  }

  get supplier() {
    return this.__supplier;
  }

  get molasses() {
    return this.__molasses;
  }

  get user() {
    return this.__user;
  }

  get fermentation() {
    return this.__fermentation;
  }

  get stripping() {
    return this.__stripping;
  }

  get spirit() {
    return this.__spirit;
  }

  get proofing() {
    return this.__proofing;
  }

  get caskandaging() {
    return this.__caskandaging;
  }

  get caskmaster() {
    return this.__caskmaster;
  }

  get bottling() {
    return this.__bottling;
  }

  get caneVariety() {
    return this.__canevariety;
  }

  get rumType() {
    return this.__rumType;
  }

  get nutrients() {
    return this.__nutrients;
  }

  get bottle() {
    return this.__bottle;
  }

  get guest() {
    return this.__guest;
  }

  get(url, ...args) {
    return this.sendRequestInternal(this.api.get, url, ...args);
  }

  post(url, ...args) {
    return this.sendRequestInternal(this.api.post, url, ...args);
  }

  patch(url, ...args) {
    return this.sendRequestInternal(this.api.patch, url, ...args);
  }

  delete(url, ...args) {
    return this.sendRequestInternal(this.api.delete, url, ...args);
  }

  put(url, ...args) {
    return this.sendRequestInternal(this.api.put, url, ...args);
  }

  sendRequestInternal(requestFunc, url, ...args) {
    logger("sendRequestInternal", url);
    const token = storage.get("token");
    if (url !== "token") {
      if (token) {
        this.api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      }

      if (
        url === "user/uploadfile" ||
        url === "farmer/uploadfile" ||
        url === "/suppliers/uploadfile"
      ) {
        this.api.defaults.headers.common["Content-Type"] =
          "multipart/form-data";
      } else {
        this.api.defaults.headers.common["Content-Type"] = "application/json";
      }
    } else {
      this.api.defaults.headers.common["Content-Type"] =
        "application/x-www-form-urlencoded";
    }
    return requestFunc(url, ...args)
      .then((response) => response.data && response.data)
      .catch((error) => {
        throw this.handleApiError(error);
      });
  }

  handleApiError(error) {
    logger(
      "handleApiError",
      typeof error?.toJSON === "function" ? error.toJSON() : error,
    );
    let errorVal = {};

    if (error.response) {
      const {
        response: { data, status },
      } = error;

      errorVal = {
        code: status ? status : API_STATUS.INTERNAL_SERVER,
        message: data?.message ? data.message : API_ERROR_MSG.BAD_REQUEST,
        status: false,
      };

      if (status === API_STATUS.UNAUTHORIZED) {
        storage.clearAll();
        if (error.config.url !== API_URLS.URL.login) {
          errorVal = {
            code: status,
            message: API_ERROR_MSG.INVALID_TOKEN,
            status: false,
          };
          setTimeout(() => {
            window.location.href = window.location.origin;
          }, 1000);
        }
      } else if (status === API_STATUS.INTERNAL_SERVER) {
        errorVal = {
          code: API_STATUS.INTERNAL_SERVER,
          message: API_ERROR_MSG.INTERNAL_SERVER_MSG,
          status: false,
        };
      }
    } else {
      errorVal = {
        code: API_STATUS.INTERNAL_SERVER,
        message: API_ERROR_MSG.INTERNAL_SERVER_MSG,
        status: false,
      };
    }

    customToast.error(errorVal.message);

    return errorVal;
  }
}

export default new API();
